import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

// Vocabulary Flashcard — AI generation step.
//
// On connect, subscribes to the ShortsWizardChannel. When the user clicks
// the "Generate" button, POSTs to the start_generation endpoint. The
// orchestrator broadcasts progress events which this controller renders
// into the progress bar + status text. Completed assets (audio URL,
// image URL) are reflected into preview elements so the teacher hears /
// sees the generated output before moving to the preview step.
export default class extends Controller {
  static values = {
    configId: Number,
    startUrl: String,
    ttsUrl: String,
    imageUrl: String,
  }

  static targets = [
    "startButton",
    "ttsButton",
    "imageButton",
    "progressBar",
    "progressFill",
    "statusStep",
    "statusMessage",
    "audioPlayer",
    "audioContainer",
    "imagePreview",
    "imageContainer",
    "error",
  ]

  connect() {
    this.subscribe()
  }

  disconnect() {
    if (this.subscription) this.subscription.unsubscribe()
  }

  subscribe() {
    const controller = this
    this.subscription = consumer.subscriptions.create(
      { channel: "ShortsWizardChannel", config_id: this.configIdValue },
      {
        received(data) { controller.handleProgress(data) },
      },
    )
  }

  handleProgress(data) {
    if (typeof data.percentage === "number" && this.hasProgressFillTarget) {
      this.progressFillTarget.style.width = `${data.percentage}%`
    }
    if (this.hasStatusStepTarget && data.step) {
      this.statusStepTarget.textContent = this.stepLabel(data.step)
    }
    if (this.hasStatusMessageTarget && data.message) {
      this.statusMessageTarget.textContent = data.message
    }
    if (data.step === "complete") {
      this.reloadAssetPreviews()
    }
  }

  stepLabel(step) {
    return {
      preparing: "Preparing",
      generate_image: "Generating image",
      generate_tts: "Generating audio",
      complete: "Complete",
    }[step] || step
  }

  async startAll(event) {
    event.preventDefault()
    this.clearError()
    this.setButtonsDisabled(true)
    this.showProgress()
    const ok = await this.post(this.startUrlValue)
    this.setButtonsDisabled(false)
    if (ok) this.reloadAssetPreviews()
  }

  async generateTts(event) {
    event.preventDefault()
    this.clearError()
    this.setButtonsDisabled(true)
    const ok = await this.post(this.ttsUrlValue)
    this.setButtonsDisabled(false)
    if (ok) this.reloadAssetPreviews()
  }

  async generateImage(event) {
    event.preventDefault()
    this.clearError()
    this.setButtonsDisabled(true)
    const ok = await this.post(this.imageUrlValue)
    this.setButtonsDisabled(false)
    if (ok) this.reloadAssetPreviews()
  }

  async post(url) {
    try {
      const response = await fetch(url, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          Accept: "application/json",
        },
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok || payload.success === false) {
        this.showError(payload.error || "Generation failed")
        return false
      }
      return true
    } catch (e) {
      this.showError(e.message || "Network error")
      return false
    }
  }

  setButtonsDisabled(disabled) {
    [this.startButtonTarget, this.ttsButtonTarget, this.imageButtonTarget]
      .forEach((btn) => { if (btn) btn.disabled = disabled })
  }

  showProgress() {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.display = ""
    }
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.style.display = ""
    this.errorTarget.textContent = message
  }

  clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.style.display = "none"
    this.errorTarget.textContent = ""
  }

  // After generation, the orchestrator persists asset URLs back into
  // config_data. Rather than re-fetch the config via a JSON endpoint,
  // we just reload the page — simple and guarantees UI correctness.
  // For smoother UX we could add a JSON endpoint in a follow-up.
  reloadAssetPreviews() {
    setTimeout(() => { window.location.reload() }, 900)
  }
}
